import React, { useEffect, useState } from "react";
import { Box, Stack, Tooltip } from "@mui/material";
import {
  RocketLaunchRounded,
  ExploreRounded,
  SpeedRounded,
  RadarRounded,
  CloseRounded,
  DarkModeRounded,
  LightModeRounded,
} from "@mui/icons-material";
import { useApp, useTheme } from "contexts";
import { EnumTheme } from "utils/enums";

type Props = {
  window?: () => Window;
};

type FlightWaypoint = {
  id: string;
  code: string;
  label: string;
  km: string;
  layer: string;
};

export function ScrollTop(_props: Props) {
  const { theme, toggleTheme } = useTheme();
  const { t } = useApp();
  const isDark = theme.id === EnumTheme.Dark;

  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeId, setActiveId] = useState("intro");
  const [mobileDeckOpen, setMobileDeckOpen] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);

  const waypoints: FlightWaypoint[] = [
    {
      id: "intro",
      code: "SEC-00",
      label: t.title?.intro || "Mission Brief",
      km: "10,000",
      layer: t.atmosphere?.exosphere || "EXOSPHERE",
    },
    {
      id: "overview",
      code: "SEC-01",
      label: t.title?.overview || "Skill Tree",
      km: "600",
      layer: t.atmosphere?.thermosphere || "THERMOSPHERE",
    },
    {
      id: "tech_arsenal",
      code: "SEC-02",
      label: t.title?.tech_arsenal || "Inventory & Loadout",
      km: "85",
      layer: t.atmosphere?.mesosphere || "MESOSPHERE",
    },
    {
      id: "project",
      code: "SEC-03",
      label: t.title?.project || "Campaign Quests",
      km: "50",
      layer: t.atmosphere?.stratosphere || "STRATOSPHERE",
    },
    {
      id: "repo",
      code: "SEC-04",
      label: t.title?.repo || "Open Source Vault",
      km: "18",
      layer: t.atmosphere?.troposphere || "TROPOSPHERE",
    },
    {
      id: "history",
      code: "SEC-05",
      label: t.title?.history || "Level Timeline",
      km: "05",
      layer: t.atmosphere?.troposphere || "TROPOSPHERE",
    },
    {
      id: "contact",
      code: "PAD-39A",
      label: t.title?.contact || t.contact?.badge || "Co-Op Terminal",
      km: "00",
      layer: t.atmosphere?.launchpad || "LAUNCHPAD",
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress =
        docHeight > 0 ? Math.min(Math.max(scrollTop / docHeight, 0), 1) : 0;
      setScrollProgress(progress);

      const sectionIds = [
        "contact",
        "history",
        "repo",
        "project",
        "tech_arsenal",
        "overview",
        "intro",
      ];
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= window.innerHeight * 0.42) {
            setActiveId(id);
            break;
          }
        }
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const computeAltitudeKm = (progress: number): string => {
    if (progress <= 0.01) return "10,000";
    if (progress >= 0.99) return "0";
    const raw = Math.round(10000 * Math.pow(1 - progress, 3.2));
    return raw.toLocaleString();
  };

  const currentAltitude = computeAltitudeKm(scrollProgress);
  const activeWaypoint =
    waypoints.find((w) => w.id === activeId) || waypoints[0];

  const handleWarpToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 24;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }
    setMobileDeckOpen(false);
  };

  const handleScrollTopLaunch = () => {
    setIsLaunching(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => {
      setIsLaunching(false);
    }, 1100);
  };

  // Visibility logic: fade out when at top (scrollProgress <= 0.06) or at very bottom (scrollProgress >= 0.94)
  const isGoToTopVisible = scrollProgress > 0.06 && scrollProgress < 0.94;

  return (
    <>
      {/* =========================================================================
          FLIGHT TELEMETRY ALTIMETER RAIL (LEFT VIEWPORT EDGE)
         ========================================================================= */}
      <Box
        component="nav"
        aria-label={t.atmosphere?.telemetry_title}
        sx={{
          display: { xs: "none", lg: "flex" },
          flexDirection: "column",
          alignItems: "flex-start",
          position: "fixed",
          left: theme.spacing.md * 8,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1200,
          pointerEvents: "auto",
          userSelect: "none",
        }}
      >
        {/* Top Digital Altimeter Readout Box */}
        <Box
          sx={{
            mb: theme.spacing.sm,
            px: theme.spacing.sm,
            py: theme.spacing.xs,
            backgroundColor: theme.color.theme.color,
            border: `2px solid ${theme.color.border.color}`,
            borderRadius: theme.border.square,
            boxShadow: `3px 3px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
            minWidth: 172,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: theme.spacing.xs,
              borderBottom: `1px dashed ${theme.color.border.color}`,
              pb: 0.5,
              mb: 0.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: theme.color.primary.color,
              }}
            >
              <SpeedRounded sx={{ fontSize: theme.font.xs }} />
              <Box
                component="span"
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: theme.font.xxxs,
                  fontWeight: theme.font.bold,
                  letterSpacing: "0.08em",
                }}
              >
                {t.atmosphere?.telemetry_title}
              </Box>
            </Box>

            {/* Quick Theme Switcher */}
            <Box
              component="button"
              onClick={toggleTheme}
              title={
                t.atmosphere?.toggle_theme
              }
              aria-label={
                t.atmosphere?.toggle_theme
              }
              sx={{
                background: "none",
                border: `1px solid ${theme.color.border.color}`,
                borderRadius: theme.border.square,
                color: theme.color.font.accent,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                p: 0.25,
                "&:hover": {
                  color: theme.color.primary.color,
                  borderColor: theme.color.primary.color,
                },
              }}
            >
              {isDark ? (
                <LightModeRounded sx={{ fontSize: 12 }} />
              ) : (
                <DarkModeRounded sx={{ fontSize: 12 }} />
              )}
            </Box>
          </Box>

          {/* Live Altitude Counter */}
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              fontFamily: '"Space Mono", monospace',
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: theme.font.xxxs,
                color: theme.color.font.accent,
              }}
            >
              {t.atmosphere?.alt_prefix || "ALT"}:
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: theme.font.sm,
                fontWeight: theme.font.bold,
                color: theme.color.font.color,
                letterSpacing: "-0.02em",
              }}
            >
              {currentAltitude}{" "}
              <Box
                component="span"
                sx={{
                  fontSize: theme.font.xxxs,
                  color: theme.color.primary.color,
                }}
              >
                {t.atmosphere?.alt_unit || "KM"}
              </Box>
            </Box>
          </Box>

          {/* Current Layer Pill */}
          <Box
            sx={{
              mt: 0.5,
              px: 0.75,
              py: 0.25,
              backgroundColor: theme.color.background.accent,
              border: `1px solid ${theme.color.border.color}`,
              fontFamily: '"Space Mono", monospace',
              fontSize: 9,
              fontWeight: theme.font.bold,
              color: theme.color.primary.color,
              textAlign: "center",
              letterSpacing: "0.06em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {activeWaypoint.layer}
          </Box>
        </Box>

        {/* Vertical Altimeter Scale */}
        <Box
          sx={{
            position: "relative",
            py: theme.spacing.xs,
            px: theme.spacing.sm,
            backgroundColor: isDark
              ? "rgba(25, 27, 34, 0.88)"
              : "rgba(255, 255, 255, 0.88)",
            backdropFilter: "blur(8px)",
            border: `1px solid ${theme.color.border.color}`,
            borderRadius: theme.border.square,
            boxShadow: `3px 3px 0px 0px ${theme.color.border.color}`,
          }}
        >
          {/* Vertical Progress Gauge Bar */}
          <Box
            sx={{
              position: "absolute",
              left: 17,
              top: 18,
              bottom: 18,
              width: 2,
              backgroundColor: theme.color.border.color,
              zIndex: 0,
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: `${scrollProgress * 100}%`,
                backgroundColor: theme.color.primary.color,
                transition: "height 0.1s linear",
              }}
            />
          </Box>

          <Stack
            spacing={theme.spacing.sm}
            sx={{ position: "relative", zIndex: 1 }}
          >
            {waypoints.map((wp) => {
              const isSelected = wp.id === activeId;
              return (
                <Box
                  key={wp.id}
                  onClick={() => handleWarpToSection(wp.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: theme.spacing.sm,
                    cursor: "pointer",
                    "&:hover .wp-label": {
                      opacity: 1,
                      transform: "translateX(2px)",
                      color: theme.color.primary.color,
                    },
                    "&:hover .wp-node": {
                      transform: "scale(1.25)",
                      borderColor: theme.color.primary.color,
                    },
                  }}
                >
                  {/* Waypoint Square Node */}
                  <Box
                    className="wp-node"
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: theme.border.square,
                      backgroundColor: isSelected
                        ? theme.color.primary.color
                        : theme.color.theme.color,
                      border: `2px solid ${isSelected
                          ? theme.color.primary.color
                          : theme.color.border.color
                        }`,
                      boxShadow: isSelected
                        ? `0 0 8px ${theme.color.primary.color}`
                        : "none",
                      transition: "all 0.15s ease",
                      flexShrink: 0,
                    }}
                  />

                  {/* Waypoint Label */}
                  <Box
                    className="wp-label"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      transition: "all 0.15s ease",
                      opacity: isSelected ? 1 : 0.65,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontFamily: '"Space Mono", monospace',
                        fontSize: theme.font.xxxs,
                        fontWeight: isSelected
                          ? theme.font.bold
                          : theme.font.medium,
                        color: isSelected
                          ? theme.color.primary.color
                          : theme.color.font.color,
                        lineHeight: 1.2,
                      }}
                    >
                      {wp.label}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontFamily: '"Space Mono", monospace',
                        fontSize: 9,
                        color: theme.color.font.accent,
                        lineHeight: 1.1,
                      }}
                    >
                      {wp.km} {t.atmosphere?.alt_unit || "KM"}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>

      {/* =========================================================================
          RIGHT-SIDE ROCKET "GO TO TOP" BUTTON
          Positioned on the RIGHT side of the screen.
          Fades OUT when at the very top (progress <= 0.06) or at the very bottom (progress >= 0.94).
          Uses a "Rocket" icon that launches skyward on click.
         ========================================================================= */}
      <Box
        sx={{
          position: "fixed",
          right: theme.spacing.lg * 8,
          bottom: theme.spacing.lg * 8,
          zIndex: 1200,
          opacity: isGoToTopVisible ? 1 : 0,
          visibility: isGoToTopVisible ? "visible" : "hidden",
          transform: isGoToTopVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 0.3s ease, transform 0.3s ease, visibility 0.3s",
          pointerEvents: isGoToTopVisible ? "auto" : "none",
        }}
      >
        <Tooltip
          title={t.atmosphere?.orbital_return}
          placement="left"
        >
          <Box
            component="button"
            onClick={handleScrollTopLaunch}
            aria-label={t.atmosphere?.orbital_return}
            sx={{
              width: 44,
              height: 44,
              backgroundColor: theme.color.primary.color,
              color: theme.color.primary.text,
              border: `2px solid ${isDark ? "#ffffff" : "#191b22"}`,
              borderRadius: theme.border.square,
              boxShadow: `3px 3px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "transform 0.15s ease, box-shadow 0.15s ease",
              "&:hover": {
                transform: "translate(-2px, -2px)",
                boxShadow: `5px 5px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
              },
              "&:active": {
                transform: "translate(1px, 1px)",
                boxShadow: "none",
              },
            }}
          >
            <RocketLaunchRounded
              sx={{
                fontSize: 22,
                transform: isLaunching
                  ? "translateY(-18px) rotate(-45deg)"
                  : "rotate(-45deg)",
                transition: "transform 0.4s ease",
              }}
            />
          </Box>
        </Tooltip>
      </Box>

      {/* =========================================================================
          MOBILE / TABLET MISSION CONTROL DOCK (BOTTOM-LEFT)
         ========================================================================= */}
      <Box
        sx={{
          display: { xs: "flex", lg: "none" },
          position: "fixed",
          bottom: theme.spacing.md * 8,
          left: theme.spacing.md * 8,
          zIndex: 1250,
          flexDirection: "column",
          alignItems: "flex-start",
          gap: theme.spacing.xs,
        }}
      >
        {mobileDeckOpen && (
          <Box
            sx={{
              mb: theme.spacing.xs,
              p: theme.spacing.md,
              width: 260,
              backgroundColor: theme.color.theme.color,
              border: `2px solid ${theme.color.border.color}`,
              borderRadius: theme.border.square,
              boxShadow: `5px 5px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pb: theme.spacing.xs,
                mb: theme.spacing.xs,
                borderBottom: `1px solid ${theme.color.border.color}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  fontFamily: '"Space Mono", monospace',
                  fontSize: theme.font.xxs,
                  fontWeight: theme.font.bold,
                  color: theme.color.primary.color,
                }}
              >
                <RadarRounded sx={{ fontSize: theme.font.sm }} />
                <span>{t.atmosphere?.cmd_deck || "MISSION CONTROL"}</span>
              </Box>
              <Box
                component="button"
                onClick={toggleTheme}
                aria-label={
                  t.atmosphere?.toggle_theme || "Toggle Day / Night Atmosphere"
                }
                sx={{
                  background: theme.color.background.accent,
                  border: `1px solid ${theme.color.border.color}`,
                  borderRadius: theme.border.square,
                  color: theme.color.font.color,
                  p: 0.5,
                  display: "inline-flex",
                  cursor: "pointer",
                }}
              >
                {isDark ? (
                  <LightModeRounded sx={{ fontSize: 14 }} />
                ) : (
                  <DarkModeRounded sx={{ fontSize: 14 }} />
                )}
              </Box>
            </Box>

            <Stack spacing={0.75}>
              {waypoints.map((wp) => {
                const isSelected = wp.id === activeId;
                return (
                  <Box
                    key={wp.id}
                    onClick={() => handleWarpToSection(wp.id)}
                    sx={{
                      px: theme.spacing.xs,
                      py: 0.75,
                      backgroundColor: isSelected
                        ? theme.color.primary.color
                        : theme.color.background.accent,
                      color: isSelected
                        ? theme.color.primary.text
                        : theme.color.font.color,
                      border: `1px solid ${theme.color.border.color}`,
                      borderRadius: theme.border.square,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      fontFamily: '"Space Mono", monospace',
                      fontSize: theme.font.xxs,
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: theme.font.bold }}>
                      {wp.label}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: theme.font.xxxs,
                        opacity: 0.85,
                      }}
                    >
                      {wp.km} {t.atmosphere?.alt_unit || "KM"}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Compact Telemetry Pill */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            component="button"
            onClick={() => setMobileDeckOpen((prev) => !prev)}
            sx={{
              px: theme.spacing.sm,
              py: theme.spacing.xs,
              backgroundColor: theme.color.theme.color,
              color: theme.color.font.color,
              border: `2px solid ${theme.color.border.color}`,
              borderRadius: theme.border.square,
              boxShadow: `3px 3px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              fontFamily: '"Space Mono", monospace',
              fontSize: theme.font.xxs,
              fontWeight: theme.font.bold,
            }}
          >
            {mobileDeckOpen ? (
              <CloseRounded sx={{ fontSize: theme.font.sm }} />
            ) : (
              <ExploreRounded
                sx={{
                  fontSize: theme.font.sm,
                  color: theme.color.primary.color,
                }}
              />
            )}
            <span>
              {currentAltitude} {t.atmosphere?.alt_unit || "KM"}
            </span>
          </Box>
        </Stack>
      </Box>
    </>
  );
}
